import React from "react";
import PropTypes from "prop-types";
import { FormFeedback } from "reactstrap";

const BaseRadioGroup = ({
  name,
  options,
  className,
  onChange,
  selectedValue,
  touched,
  error,
  handleBlur,
  disabled,
  inline,
}) => {
  return (
    <div
      className={className}
      style={{
        display: "flex",
        flexDirection: inline ? "row" : "column",
        gap: 10,
        flexWrap: "wrap",
      }}
    >
      {options?.map((option, index) => {
        const isSelected = selectedValue === option.value;
        return (
          <label
            key={index}
            htmlFor={option.id}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "10px 14px",
              borderRadius: 10,
              cursor: disabled ? "not-allowed" : "pointer",
              border: `1.5px solid ${isSelected ? "rgba(99,102,241,0.4)" : "var(--glass-border)"}`,
              background: isSelected ? "rgba(99,102,241,0.06)" : "var(--icon-btn-bg)",
              opacity: disabled ? 0.6 : 1,
              transition: "all 0.15s",
              flex: inline ? "1 1 auto" : "none",
            }}
          >
            {/* Hidden native input */}
            <input
              id={option.id}
              type="radio"
              name={name}
              value={option.value}
              checked={isSelected}
              onChange={onChange}
              onBlur={handleBlur}
              disabled={disabled}
              style={{ position: "absolute", opacity: 0, width: 0, height: 0 }}
            />
            {/* Custom radio circle */}
            <div
              style={{
                width: 18,
                height: 18,
                borderRadius: "50%",
                flexShrink: 0,
                border: `2px solid ${isSelected ? "var(--brand-indigo)" : "var(--glass-border)"}`,
                background: isSelected ? "var(--brand-indigo)" : "transparent",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.15s",
              }}
            >
              {isSelected && (
                <div
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: "#fff",
                  }}
                />
              )}
            </div>
            {/* Label text */}
            <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <span
                style={{
                  fontSize: "0.875rem",
                  fontWeight: isSelected ? 600 : 500,
                  color: isSelected ? "var(--brand-indigo)" : "var(--text-primary)",
                  lineHeight: 1.4,
                }}
              >
                {option.label}
              </span>
              {option.labelSuffix && (
                <span
                  style={{
                    fontSize: "0.75rem",
                    color: "var(--text-muted)",
                    lineHeight: 1.3,
                  }}
                >
                  {option.labelSuffix}
                </span>
              )}
            </div>
          </label>
        );
      })}
      {touched && error && <FormFeedback className="d-block">{error}</FormFeedback>}
    </div>
  );
};

BaseRadioGroup.propTypes = {
  name: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
      labelSuffix: PropTypes.node,
    }),
  ).isRequired,
  className: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  selectedValue: PropTypes.string,
  touched: PropTypes.bool,
  error: PropTypes.string,
  handleBlur: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  inline: PropTypes.bool,
};

BaseRadioGroup.defaultProps = {
  className: "",
  selectedValue: "",
  disabled: false,
  inline: false,
};

export default BaseRadioGroup;
